import axios from "axios";
import alert_mixin from "@/utils/mixins/alert_mixin";
import {
  GET_ASSIGNED_QUESTIONS_URL,
  ASSIGN_QUESTION_TO_CONDITION_URL,
  UNASSIGN_QUESTION_FROM_CONDITION_URL,
  GET_CSRF_TOKEN
} from "@/utils/constants/constants";

export default {
  name: "ConditionQuestionAssignment",
  mixins: [alert_mixin],

  props: {
    selected_rule: Object,
    selected_condition: Object,
    form_id: [String, Number],
    is_visible: Boolean
  },

  data: () => ({
    questions: [],
    question_search: "",
    csrfToken: "",
    token: null,
    is_loading: false,
    pending_changes: [],
  }),

  computed: {
    filtered_questions() {
      if (!this.question_search.trim()) {
        return this.questions;
      }

      const search = this.question_search.toLowerCase();

      return this.questions.filter(q => {
        return (
          (q.question_text || "").toLowerCase().includes(search) ||
          (q.form_name || "").toLowerCase().includes(search) ||
          this.formatQuestionType(q.question_type)
            .toLowerCase()
            .includes(search)
        );
      });
    },
    
    hasSelectQuestions() {
      return this.questions.some(q => this.isSelectQuestion(q.question_type));
    },
    
    hasIncompleteSelectAssignments() {

      return this.pending_changes.some(change => {
        if (!change.should_assign) return false;
        
        const question = this.questions.find(q => q.id === change.question_id);
        if (!question || !this.isSelectQuestion(question.question_type)) return false;
        
        if (this.isSingleSelect(question.question_type)) {
          return !change.selected_option_id;
        }
        
        if (this.isMultiSelect(question.question_type)) {
          return !change.selected_option_ids || change.selected_option_ids.length === 0;
        }
        
        return false;
      });
    },
    
    hasPendingChanges() {
      return this.pending_changes.length > 0;
    }
  },

  watch: {
    is_visible(val) {
      if (val) {
        this.fetchCsrfToken();
        this.loadQuestions();

        this.$nextTick(() => {
          this.$refs.modalElement?.focus();
        });
      }
    },

    selected_condition: {
      handler() {
        if (this.is_visible) {
          this.loadQuestions();
        }
      },
      deep: true
    }
  },

  methods: {
    attemptClose() {
      if (this.pending_changes.length > 0) {
        if (confirm("You have unsaved changes. Close without saving?")) {
          this.$emit("close");
        }
      } else {
        this.$emit("close");
      }
    },

    fetchCsrfToken() {
      axios
        .get(GET_CSRF_TOKEN, { withCredentials: true })
        .then(response => {
          this.csrfToken = response.data.csrfToken || response.data.token;
        });
    },

    loadQuestions() {
      if (!this.selected_rule) {
        this.questions = [];
        return;
      }

      this.is_loading = true;

      this.token =
        sessionStorage.getItem("token") ||
        localStorage.getItem("token");

      axios
        .get(GET_ASSIGNED_QUESTIONS_URL, {
          headers: this.getHeaders(),
          params: { rule_id: this.selected_rule.id },
          withCredentials: true
        })
        .then(response => {
          if (response.data.status !== "success") {
            this.questions = [];
            return;
          }
          
          this.questions = this.processQuestionsWithDependents(
            response.data.questions
          );
          
          this.updateQuestionAssignments();
          
          this.initializeSelectedOptions();
          
          this.pending_changes = [];
        })
        .catch(() => {
          this.questions = [];
        })
        .finally(() => {
          this.is_loading = false;
        });
    },

    updateQuestionAssignments() {
  this.questions.forEach(question => {
    const currentAssignment = question.field_assignments?.find(
      assignment => assignment.compliance_condition_id === this.selected_condition.id
    ) || (question.condition && question.condition.id === this.selected_condition.id ? question.condition : null);

    const isCurrentCondition = !!currentAssignment;
    
    question.is_assigned = isCurrentCondition;
    question.assigned_to_condition = isCurrentCondition;
    
    if (question.dependent_questions) {
      question.dependent_questions.forEach(dep => {
        dep.assigned_to_condition = isCurrentCondition;
      });
    }
    
    if (isCurrentCondition && this.isSelectQuestion(question.question_type)) {
      this.setSelectedOptionsFromCondition(question, currentAssignment);
    }
  });
},

    setSelectedOptionsFromCondition(question, assignmentSource) {
  const conditionData = assignmentSource.compliance_condition || assignmentSource;
  
  if (!conditionData || !conditionData.selected_options) return;
  
  const selectedOptions = conditionData.selected_options;
  
  if (this.isSingleSelect(question.question_type) && selectedOptions.length > 0) {
    question.selected_option_id = selectedOptions[0].id;
  }
  
  if (this.isMultiSelect(question.question_type)) {
    question.selected_option_ids = selectedOptions.map(option => option.id);
  }
},
    
    initializeSelectedOptions() {
      this.questions.forEach(q => {
        if (this.isSelectQuestion(q.question_type)) {
          const hasCondition = q.condition !== null;
          const isCurrentCondition = hasCondition && 
                                   this.selected_condition && 
                                   q.condition.id === this.selected_condition.id;
          
          if (isCurrentCondition && q.condition && q.condition.selected_options) {
            const selectedOptions = q.condition.selected_options;
            
            if (this.isSingleSelect(q.question_type) && selectedOptions.length > 0) {
              q.selected_option_id = selectedOptions[0].id;
            } else if (this.isMultiSelect(q.question_type)) {
              q.selected_option_ids = selectedOptions.map(option => option.id);
            }
          } else {
            if (this.isSingleSelect(q.question_type)) {
              q.selected_option_id = q.selected_option_id || null;
            }
            if (this.isMultiSelect(q.question_type)) {
              q.selected_option_ids = Array.isArray(q.selected_option_ids) ? q.selected_option_ids : [];
            }
          }
        }
      });
    },
    
    isSelectQuestion(questionType) {
      if (!questionType) return false;
      const typeName = typeof questionType === 'object' ? questionType.name : questionType;
      return ['single_select', 'multi_select', 'dropdown', 'radio', 'checkbox'].some(
        selectType => typeName.toLowerCase().includes(selectType)
      );
    },
    
    isSingleSelect(questionType) {
      if (!questionType) return false;
      const typeName = typeof questionType === 'object' ? questionType.name : questionType;
      return ['single_select', 'dropdown', 'radio'].some(
        selectType => typeName.toLowerCase().includes(selectType)
      );
    },
    
    isMultiSelect(questionType) {
      if (!questionType) return false;
      const typeName = typeof questionType === 'object' ? questionType.name : questionType;
      return ['multi_select', 'checkbox'].some(
        selectType => typeName.toLowerCase().includes(selectType)
      );
    },
    
    isQuestionInPendingChanges(question) {
      return this.pending_changes.some(change => change.question_id === question.id);
    },

    processQuestionsWithDependents(questions) {
      const mainQuestions = [];
      const dependentMap = new Map();

      questions.forEach(q => {
        this._processQuestion(q, mainQuestions, dependentMap);
      });

      this._attachDependentQuestions(mainQuestions, dependentMap);
      return mainQuestions;
    },

    _processQuestion(question, mainQuestions, dependentMap) {
      const parentId = this._getParentId(question);
      const isAssigned = this._isQuestionAssigned(question);

      if (this._isDependentQuestion(parentId)) {
        this._addToDependentMap(dependentMap, parentId, question, isAssigned);
      } else {
        this._addToMainQuestions(mainQuestions, question, isAssigned);
      }
    },

    _getParentId(question) {
      return question.depends_on_question ??
             question.depends_on_question_id ??
             question.parent_question_id;
    },

    _isQuestionAssigned(question) {
      const hasCondition = question.condition !== null;
      const isCurrentCondition = hasCondition && 
                               this.selected_condition && 
                               question.condition.id === this.selected_condition.id;
      
      return isCurrentCondition || 
             question.assigned_to_condition === true ||
             question.is_assigned === true;
    },

    _isDependentQuestion(parentId) {
      return Number.isInteger(parentId) && parentId > 0;
    },

    _addToDependentMap(dependentMap, parentId, question, isAssigned) {
      if (!dependentMap.has(parentId)) {
        dependentMap.set(parentId, []);
      }

      dependentMap.get(parentId).push({
        id: question.id,
        compliance_field_id: question.compliance_field_id,
        question_text: question.question_text,
        question_type: question.question_type,
        question_number: question.question_number,
        assigned_to_condition: isAssigned
      });
    },

    _addToMainQuestions(mainQuestions, question, isAssigned) {
      const questionData = {
        id: question.id,
        compliance_field_id: question.compliance_field_id,
        question_text: question.question_text,
        question_type: question.question_type,
        question_number: question.question_number,
        form_name: question.form_name,
        assigned_to_condition: isAssigned,
        is_assigned: isAssigned,
        condition: question.condition,
        options: question.options || [],
        selected_option_id: question.selected_option_id || null,
        selected_option_ids: Array.isArray(question.selected_option_ids) ? question.selected_option_ids : [],
        dependent_questions: []
      };
      
      mainQuestions.push(questionData);
    },

    _attachDependentQuestions(mainQuestions, dependentMap) {
      mainQuestions.forEach(q => {
        if (dependentMap.has(q.id)) {
          q.dependent_questions = dependentMap.get(q.id);
        }
      });
    },

    formatQuestionType(type) {
      if (!type) return "Unknown";
      if (typeof type === "object") return type.name || "Unknown";
      return type;
    },

    getHeaders() {
      const headers = { "Content-Type": "application/json" };
      if (this.token) headers.Authorization = `Token ${this.token}`;
      if (this.csrfToken) headers["X-CSRFToken"] = this.csrfToken;
      return headers;
    },

    onToggleAssign(question) {
      question.is_assigned = !question.is_assigned;
      
      if (this.isSelectQuestion(question.question_type)) {
        if (question.is_assigned) {
          question.selected_option_id = null;
          question.selected_option_ids = [];
          
          this.trackChange(question, true);
        } else {
          this.removeFromPendingChanges(question);
          question.assigned_to_condition = false;
          
          this.trackChange(question, false);
        }
      } else {
        question.assigned_to_condition = question.is_assigned;
        this.trackChange(question, question.is_assigned);
      }

      if (question.dependent_questions?.length) {
        question.dependent_questions.forEach(dep => {
          dep.assigned_to_condition = question.is_assigned;
          
          const existingChange = this.pending_changes.find(c => c.question_id === dep.id);
          if (existingChange) {
            existingChange.should_assign = question.is_assigned;
          } else {
            this.pending_changes.push({
              question_id: dep.id,
              compliance_field_id: dep.compliance_field_id,
              should_assign: question.is_assigned,
              is_main_question: false
            });
          }
        });
      }
    },
    
    removeFromPendingChanges(question) {
      this.pending_changes = this.pending_changes.filter(
        c => c.question_id !== question.id
      );
      
      // Also remove any dependent questions
      if (question.dependent_questions?.length) {
        question.dependent_questions.forEach(dep => {
          this.pending_changes = this.pending_changes.filter(
            c => c.question_id !== dep.id
          );
        });
      }
    },
    
    onOptionSelectionChange(question) {
      const changeIndex = this.pending_changes.findIndex(c => c.question_id === question.id);
      
      if (changeIndex !== -1) {
        const change = this.pending_changes.at(changeIndex);
        
        if (change) {
          if (this.isSingleSelect(question.question_type)) {
            change.selected_option_id = question.selected_option_id;
          } else if (this.isMultiSelect(question.question_type)) {
            change.selected_option_ids = [...(question.selected_option_ids || [])];
          }
        }
      } else {
        this.trackChange(question, true);
      }
    },

    trackChange(question, shouldAssign) {
      // Remove existing change for this question
      this.pending_changes = this.pending_changes.filter(
        c => c.question_id !== question.id
      );

      const changeData = {
        question_id: question.id,
        compliance_field_id: question.compliance_field_id,
        should_assign: shouldAssign,
        is_main_question: true
      };
      
      if (shouldAssign && this.isSelectQuestion(question.question_type)) {
        if (this.isSingleSelect(question.question_type)) {
          changeData.selected_option_id = question.selected_option_id;
        } else if (this.isMultiSelect(question.question_type)) {
          changeData.selected_option_ids = [...(question.selected_option_ids || [])];
        }
      }

      this.pending_changes.push(changeData);

      question.dependent_questions?.forEach(dep => {
        this.pending_changes = this.pending_changes.filter(
          c => c.question_id !== dep.id
        );

        this.pending_changes.push({
          question_id: dep.id,
          compliance_field_id: dep.compliance_field_id,
          should_assign: shouldAssign,
          is_main_question: false
        });
      });
    },

    saveAssignments() {
      if (!this.selected_condition || !this.selected_rule) {
        this.show_error("No condition or rule selected");
        return;
      }
      
      if (this.pending_changes.length === 0) {
        this.show_error("No changes to save");
        this.$emit("close");
        return;
      }
      const incompleteAssignments = this.pending_changes.filter(change => {
        if (!change.should_assign) return false;
        
        const question = this.questions.find(q => q.id === change.question_id);
        if (!question || !this.isSelectQuestion(question.question_type)) return false;
        
        if (this.isSingleSelect(question.question_type)) {
          return !change.selected_option_id;
        }
        
        if (this.isMultiSelect(question.question_type)) {
          return !change.selected_option_ids || change.selected_option_ids.length === 0;
        }
        
        return false;
      });

      if (incompleteAssignments.length > 0) {
        this.show_error("Please select options for all select questions you are assigning");
        return;
      }

      const assignCount = this.pending_changes.filter(c => c.should_assign).length;
      const unassignCount = this.pending_changes.filter(c => !c.should_assign).length;
      
      const changesToProcess = [...this.pending_changes];
      
      const calls = changesToProcess.map(change =>
        change.should_assign
          ? this.assignQuestionToCondition(
              change.question_id, 
              change.selected_option_id, 
              change.selected_option_ids,
              this.selected_rule.id
            )
          : this.unassignQuestionFromCondition(
              change.question_id,
              this.selected_rule.id
            )
      );

      Promise.all(calls)
        .then(() => {
          this.updateQuestionsAfterSave(changesToProcess);
          
          this.pending_changes = [];
          
          let message = "Assignments saved successfully";
          if (assignCount > 0 && unassignCount > 0) {
            message = `${assignCount} questions assigned, ${unassignCount} questions unassigned successfully`;
          } else if (assignCount > 0) {
            message = `${assignCount} question${assignCount > 1 ? 's' : ''} assigned successfully`;
          } else if (unassignCount > 0) {
            message = `${unassignCount} question${unassignCount > 1 ? 's' : ''} unassigned successfully`;
          }
          this.show_success(message);
          
          this.$emit("assignments-saved");
        })
        .catch(error => {
          // Restore pending changes if save fails
          this.pending_changes = changesToProcess;
          
          const errorMessage = error.response?.data?.message || error.message || "Failed to save assignments";
          this.show_error(errorMessage);
          console.error("Save error:", error);
          
          this.$emit("save-error", error);
        });
    },

    updateQuestionsAfterSave(changes) {
      changes.forEach(change => {
        const question = this.questions.find(q => q.id === change.question_id);
        if (question) {
          if (change.should_assign) {
            question.is_assigned = true;
            question.assigned_to_condition = true;
            
            question.condition = {
              id: this.selected_condition.id,
              condition_type: {
                id: this.selected_condition.condition_type?.id || 1,
                name: this.selected_condition.condition_type?.name || "question_response"
              },
              condition_operator: {
                id: this.selected_condition.condition_operator?.id || 8,
                name: this.selected_condition.condition_operator?.name || "contains"
              },
              value: this.selected_condition.value || "",
              value_max: this.selected_condition.value_max || null,
              selected_options: this.getSelectedOptionsFromChange(question, change),
              version: 1
            };
            
            if (this.isSelectQuestion(question.question_type)) {
              if (this.isSingleSelect(question.question_type) && change.selected_option_id) {
                question.selected_option_id = change.selected_option_id;
              }
              if (this.isMultiSelect(question.question_type) && change.selected_option_ids) {
                question.selected_option_ids = [...change.selected_option_ids];
              }
            }
          } else {
            question.is_assigned = false;
            question.assigned_to_condition = false;
            question.condition = null;
            
            if (this.isSelectQuestion(question.question_type)) {
              if (this.isSingleSelect(question.question_type)) {
                question.selected_option_id = null;
              }
              if (this.isMultiSelect(question.question_type)) {
                question.selected_option_ids = [];
              }
            }
          }
        }
      });
    },

    getSelectedOptionsFromChange(question, change) {
      if (!this.isSelectQuestion(question.question_type)) {
        return [];
      }
      
      const selectedOptions = [];
      
      if (this.isSingleSelect(question.question_type) && change.selected_option_id) {
        const option = question.options?.find(o => o.id === change.selected_option_id);
        if (option) {
          selectedOptions.push({ id: option.id, label: option.label });
        }
      }
      
      if (this.isMultiSelect(question.question_type) && change.selected_option_ids) {
        change.selected_option_ids.forEach(optionId => {
          const option = question.options?.find(o => o.id === optionId);
          if (option) {
            selectedOptions.push({ id: option.id, label: option.label });
          }
        });
      }
      
      return selectedOptions;
    },

    assignQuestionToCondition(questionId, selectedOptionId = null, selectedOptionIds = [], ruleId = null) {
      const question = this.questions.find(q => q.id === questionId);
      
      if (!question) {
        throw new Error("Question not found");
      }
      
      const questionIdForAssign = questionId;
      
      const payload = {
        condition_id: this.selected_condition.id,
        question_id: questionIdForAssign,
        rule_id: ruleId || this.selected_rule.id
      };

      if (selectedOptionId) {
        const option = this.questions
          .find(q => q.id === questionId)
          ?.options.find(o => o.id === selectedOptionId);

        if (option) {
          payload.selected_options = [
            { id: option.id, label: option.label }
          ];
        }
      }

      if (selectedOptionIds && selectedOptionIds.length > 0) {
        const options = this.questions
          .find(q => q.id === questionId)
          ?.options.filter(o => selectedOptionIds.includes(o.id));

        if (options?.length) {
          payload.selected_options = options.map(o => ({
            id: o.id,
            label: o.label
          }));
        }
      }


      return axios.post(
        ASSIGN_QUESTION_TO_CONDITION_URL,
        payload,
        {
          headers: this.getHeaders(),
          withCredentials: true
        }
      ).then(response => {
        if (response.data.status === "success") {
          return response.data;
        } else {
          throw new Error(response.data.message || "Failed to assign question");
        }
      });
    },

    unassignQuestionFromCondition(questionId, ruleId = null) {
      const question = this.questions.find(q => q.id === questionId);
      
      if (!question) {
        throw new Error("Question not found");
      }
      
      const complianceFieldId = question.compliance_field_id;
      
      if (!complianceFieldId) {
        throw new Error("compliance_field_id not found for question");
      }
      
      const payload = {
        condition_id: this.selected_condition.id,
        compliance_field_id: complianceFieldId,
        rule_id: ruleId || this.selected_rule.id
      };

      return axios.post(
        UNASSIGN_QUESTION_FROM_CONDITION_URL,
        payload,
        {
          headers: this.getHeaders(),
          withCredentials: true
        }
      ).then(response => {
        if (response.data.status === "success") {
          return response.data;
        } else {
          throw new Error(response.data.message || "Failed to unassign question");
        }
      });
    }
  }
};